import styled from '@emotion/styled';
import {
  fetchCarrierParcelTemplates,
  fetchParcelTemplates,
} from '~/actions/parcel-template.actions';
import { CheckboxField } from '~/components/Checkbox/Checkbox';
import FormError from '~/components/FormError/FormError';
import { InputExtendedField, InputFieldProps, Prefix } from '~/components/InputExtended';
import { Option, options, SelectField } from '~/components/Select';
import { HeightSize, InputSize } from '~/constants/form';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import { ParcelSelectFieldContainer } from '~/containers/ParcelSelectContainer';
import * as React from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import {
  change,
  Field,
  FormSection,
  GenericField,
  getFormMeta,
  getFormSyncErrors,
  getFormValues,
  initialize,
} from 'redux-form';
import { getUserConfigDefaults } from '~/selectors/user.selectors';
import { colors, formSizes } from '~/styles/constants';
import CarrierParcelTemplate from '~/types/CarrierParcelTemplate';
import { getDimensionsString, isParcelTemplate } from '~/types/ParcelTemplate';
import { TrackingAppLocation } from '~/utils/TrackingHelper/types';
import { InjectedTranslateProps, translate } from '~/utils/translate';
import { ApplicationState } from '~/store';
import PackageTemplateRadioGroup from './PackageTemplateRadioGroup';
import { PackageTemplateChooserFormValues } from './types';
import { allowKeyboardNumberInput } from '~/pages/Orders/Blackbeard/LabelPurchase/modules/PackageInfoCard/PackageForm/Sections/Fields/util';

const InlineFieldContainer = styled('div')`
  margin-left: 10px;
  display: flex;
  align-items: center;
`;

const MaxContentLabel = styled('div')`
  width: max-content;
`;

const FieldsWrapper = styled('div')`
  display: flex;
  margin: 8px 0 8px 0;
  align-items: flex-start;
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    flex-wrap: wrap;
  }
`;

const XContainer = styled('div')`
  line-height: ${formSizes.inputHeight};
  width: 8px;
  margin: 0 10px;
`;

const FieldLabel = styled('div')`
  color: ${colors.gray700};
  font-weight: normal;
  margin: 15px 0 0 0;
`;

const Subtext = styled('div')`
  display: flex;
  align-items: center;
  color: ${colors.gray400};
  font-size: 0.75rem;
  line-height: 1.25rem;
`;

interface Props {
  formName: string;
  disableDropdown?: boolean;
  /**
   * If true, then such templates are not available for selection. Otherwise, they are with read-only dimensions
   */
  editingCustomTemplate?: boolean;
  isWeightRequired?: boolean;
  startingTabIndex?: number;
  someOrderHasImportedWeight: boolean;
  showZeroState?: boolean;
  importedWeight?: Maybe<string>;
  importedWeightOption?: Option;
  dataAnalyticsContext?: string;
  shouldShowPolymailer?: boolean;
  getCurrentDistanceUnit?: Option;
  appLocation: TrackingAppLocation;
}

interface PackageRadioState {
  packageDimensionType?: Maybe<Option>;
  shouldShowRadioGroup?: boolean;
}

type ComponentProps = Props &
  InjectedTranslateProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const InputExtendedReduxFormField = Field as new () => GenericField<InputFieldProps>;

/**
 * Warning: this component doesn't handle being inside a FormSection right now.
 */
export class PackageTemplateChooser extends React.Component<ComponentProps, PackageRadioState> {
  private weightInput = React.createRef<HTMLInputElement>();

  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      packageDimensionType: null,
      shouldShowRadioGroup: true,
    };
  }

  packageTypesOptions = [
    {
      label: this.props.t('orders:packageDimensionTypes:boxRadioLabel'),
      value: 'box',
    },
    {
      label: this.props.t('orders:packageDimensionTypes:boxPolymailerLabel'),
      value: 'polymailer',
      addToolTip: true,
    },
  ];

  componentDidMount() {
    const {
      editingCustomTemplate,
      selectedParcelTemplate,
      onCustomDimensionsSelected,
      onCarrierTemplateWithCustomDimensionsSelected,
    } = this.props;

    this.props.fetchCarrierParcelTemplates();

    // get polymailer heights for selected unit
    const selectedPolymailerHeightInches =
      selectedParcelTemplate?.distanceUnit &&
      selectedParcelTemplate?.distanceUnit === 'in' &&
      selectedParcelTemplate?.height &&
      selectedParcelTemplate.height === 0.25
        ? true
        : false;
    const selectedPolymailerHeightCm =
      selectedParcelTemplate?.distanceUnit &&
      selectedParcelTemplate?.distanceUnit === 'cm' &&
      selectedParcelTemplate?.height &&
      selectedParcelTemplate.height === 0.635
        ? true
        : false;
    const selectedPolymailerHeightMm =
      selectedParcelTemplate?.distanceUnit &&
      selectedParcelTemplate?.distanceUnit === 'mm' &&
      selectedParcelTemplate?.height &&
      selectedParcelTemplate.height === 6.35
        ? true
        : false;
    if (
      selectedPolymailerHeightInches ||
      selectedPolymailerHeightCm ||
      selectedPolymailerHeightMm
    ) {
      this.setState({
        packageDimensionType: this.packageTypesOptions[1],
      });
    }

    if (editingCustomTemplate) {
      if (
        !selectedParcelTemplate ||
        (selectedParcelTemplate &&
          isParcelTemplate(selectedParcelTemplate) &&
          selectedParcelTemplate.carrierTemplate === null)
      ) {
        // Editing template mode, template not based on a carrier template
        onCustomDimensionsSelected(true);
        this.setState({
          shouldShowRadioGroup: true,
        });
      } else if (
        // editing a user template based on variable dimension carrier template
        selectedParcelTemplate &&
        isParcelTemplate(selectedParcelTemplate) &&
        selectedParcelTemplate.carrierTemplate &&
        (selectedParcelTemplate.carrierTemplate as CarrierParcelTemplate).isVariableDimensions
      ) {
        onCarrierTemplateWithCustomDimensionsSelected(true);
      }
    } else {
      this.props.fetchParcelTemplates();

      if (!selectedParcelTemplate) {
        // Not editing mode, and template isn't existing one (parcel with custom dimensions gets turned into template too)
        onCustomDimensionsSelected(true);
      }
    }

    if (this.props.isWeightRequired) this.props.changeIsWeightRequired(this.props.isWeightRequired);
    if (
      selectedParcelTemplate &&
      isParcelTemplate(selectedParcelTemplate) &&
      selectedParcelTemplate.carrierTemplate !== null
    ) {
      this.setState({
        shouldShowRadioGroup: false,
      });
    }
  }

  componentDidUpdate(prevProps: ComponentProps) {
    const { getCurrentDistanceUnit } = this.props;

    if (prevProps.getCurrentDistanceUnit !== getCurrentDistanceUnit) {
      if (this.state?.packageDimensionType?.value === 'polymailer') {
        const setHeightValue = this.getHeightValueByDistanceUnit();
        this.props.changePackageHeight(setHeightValue);
      }
    }
  }

  onUseImportedWeightChange = () => {
    if (!isNaN(Number(this.props.importedWeight))) {
      this.props.changePackageWeight(this.props.importedWeight?.toString() || '');
    } else {
      this.props.changePackageWeight('');
    }
    if (this.props.importedWeightOption) {
      this.props.changePackageWeightUnit(this.props.importedWeightOption);
    }
  };

  handleCustomDimensionsSelected = () => this.props.onCustomDimensionsSelected(true);

  onParcelTemplateSelected = (value) => {
    if (!value) {
      this.setState({
        shouldShowRadioGroup: true,
      });
      this.props.removeSelectedParcelTemplate();
      this.props.onCarrierTemplateWithCustomDimensionsSelected(false);

      return this.props.onCustomDimensionsSelected(true);
    }

    // if it has a name, then it's a carrier or a parcel template
    if (value.name || (value.carrierTemplate && value.carrierTemplate.name)) {
      this.setState({
        shouldShowRadioGroup: false,
      });

      if (value.isVariableDimensions) {
        this.props.onCarrierTemplateWithCustomDimensionsSelected(true);
        this.props.onCustomDimensionsSelected(false);
      } else {
        this.weightInput.current?.focus();
        this.props.onCarrierTemplateWithCustomDimensionsSelected(false);
        this.props.onCustomDimensionsSelected(false);
      }

      if (value.weight && value.weightUnit) {
        this.props.changePackageWeight(value.weight.toString());
        this.props.changePackageWeightUnit(options.commonWeightOptions[value.weightUnit]);
        // This parcel had weight so uncheck use imported weight
        this.props.changeUseImportedWeight(false);
      }
    }
  };

  renderDimensions() {
    const {
      isUsingCustomDimensions,
      selectedParcelTemplate,
      startingTabIndex,
      carrierTemplatewithCustomDimensions,
      dataAnalyticsContext,
      t,
    } = this.props;
    let dimensions: Maybe<JSX.Element> = null;

    if (isUsingCustomDimensions || carrierTemplatewithCustomDimensions) {
      const { formMetaData, formSyncErrors } = this.props;

      // Form validation errors come from either parcel template form, or
      // custom dimensions form.
      const { customDimensions } = formMetaData;
      const lengthTouched = customDimensions?.length?.touched;
      const widthTouched = customDimensions?.width?.touched;
      const heightTouched = customDimensions?.height?.touched;
      const unitTouched = customDimensions?.distanceUnit?.touched;

      let errors: Maybe<JSX.Element> = null;

      if (lengthTouched && formSyncErrors.customDimensions?.length) {
        const errorMessage = formSyncErrors.customDimensions.length;
        errors = <FormError>{errorMessage}</FormError>;
      } else if (widthTouched && formSyncErrors.customDimensions?.width) {
        const errorMessage = formSyncErrors.customDimensions.width;
        errors = <FormError>{errorMessage}</FormError>;
      } else if (heightTouched && formSyncErrors.customDimensions?.height) {
        const errorMessage = formSyncErrors.customDimensions.height;
        errors = <FormError>{errorMessage}</FormError>;
      } else if (unitTouched && formSyncErrors.customDimensions?.distanceUnit) {
        const errorMessage = formSyncErrors.customDimensions.distanceUnit;
        errors = <FormError>{errorMessage}</FormError>;
      }

      const renderPrefixWithText = (text) => () => <Prefix>{text}</Prefix>;
      dimensions = carrierTemplatewithCustomDimensions ? (
        <FormSection name="customDimensions">
          <FieldLabel>{t('orders:calculateRates:tabs:shipmentInfo:dimensions')}</FieldLabel>
          <FieldsWrapper>
            <InputExtendedReduxFormField
              name="length"
              component={InputExtendedField}
              // @NOTE: @react-17-upgrade - the follow props are not a part of the component implemented here
              // size={InputSize.xs}
              // hideErrors
              // autofocus
              tabIndex={startingTabIndex}
              renderPrefix={renderPrefixWithText('L')}
              dataAnalytics={
                dataAnalyticsContext &&
                `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthInput`
              }
              isResponsive
              isRightAligned
              inputMode="decimal"
            />
            <XContainer>X</XContainer>
            <Field
              name="width"
              hideErrors
              component={InputExtendedField}
              size={InputSize.xs}
              tabIndex={startingTabIndex}
              renderPrefix={renderPrefixWithText('W')}
              isResponsive
              isRightAligned
              dataAnalytics={
                dataAnalyticsContext &&
                `${dataAnalyticsContext}PackageInfoCustomDimensions-click-widthInput`
              }
              dataE2E="parcel-form-customdims-width"
              inputMode="decimal"
            />
            <InlineFieldContainer>
              <Field
                name="distanceUnit"
                hideErrors
                component={SelectField}
                height={HeightSize.medium}
                options={options.distanceOptions}
                placeholder="Unit"
                size={InputSize.xs}
                tabIndex={startingTabIndex}
                dataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthUnitsSelect`
                }
                optionDataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthUnitOption`
                }
              />
            </InlineFieldContainer>
          </FieldsWrapper>
          <Subtext>{t('orders:calculateRates:tabs:shipmentInfo:customDimensionsSubtext')}</Subtext>
          {errors}
        </FormSection>
      ) : (
        <FormSection name="customDimensions">
          <FieldLabel>{t('orders:calculateRates:tabs:shipmentInfo:dimensions')}</FieldLabel>
          <FieldsWrapper>
            <Field
              name="length"
              component={InputExtendedField}
              size={InputSize.xs}
              hideErrors
              autofocus
              tabIndex={startingTabIndex}
              renderPrefix={renderPrefixWithText('L')}
              isResponsive
              isRightAligned
              dataE2E="parcel-form-customdims-length"
              onKeyDown={allowKeyboardNumberInput}
              dataAnalytics={
                dataAnalyticsContext &&
                `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthInput`
              }
              inputMode="decimal"
            />
            <XContainer>X</XContainer>
            <Field
              name="width"
              hideErrors
              component={InputExtendedField}
              size={InputSize.xs}
              tabIndex={startingTabIndex}
              renderPrefix={renderPrefixWithText('W')}
              isResponsive
              isRightAligned
              dataE2E="parcel-form-customdims-width"
              onKeyDown={allowKeyboardNumberInput}
              dataAnalytics={
                dataAnalyticsContext &&
                `${dataAnalyticsContext}PackageInfoCustomDimensions-click-widthInput`
              }
              inputMode="decimal"
            />
            {(this.state.packageDimensionType?.value !== 'polymailer' ||
              !this.props.shouldShowPolymailer) && (
              <>
                <XContainer>X</XContainer>
                <Field
                  name="height"
                  hideErrors
                  component={InputExtendedField}
                  size={InputSize.xs}
                  tabIndex={startingTabIndex}
                  renderPrefix={renderPrefixWithText('H')}
                  isResponsive
                  isRightAligned
                  dataE2E="parcel-form-customdims-height"
                  onKeyDown={allowKeyboardNumberInput}
                  dataAnalytics={
                    dataAnalyticsContext &&
                    `${dataAnalyticsContext}PackageInfoCustomDimensions-click-heightInput`
                  }
                  inputMode="decimal"
                />
              </>
            )}
            <InlineFieldContainer>
              <Field
                name="distanceUnit"
                hideErrors
                component={SelectField}
                height={HeightSize.medium}
                options={options.distanceOptions}
                placeholder="Unit"
                size={InputSize.xs}
                tabIndex={startingTabIndex}
                dataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthUnitsSelect`
                }
                optionDataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}PackageInfoCustomDimensions-click-lengthUnitOption`
                }
              />
            </InlineFieldContainer>
          </FieldsWrapper>
          <Subtext>{t('orders:calculateRates:tabs:shipmentInfo:customDimensionsSubtext')}</Subtext>
          {errors}
        </FormSection>
      );
    } else if (selectedParcelTemplate) {
      const dimensionsProvider =
        isParcelTemplate(selectedParcelTemplate) &&
        selectedParcelTemplate.carrierTemplate &&
        !selectedParcelTemplate.carrierTemplate.isVariableDimensions
          ? selectedParcelTemplate.carrierTemplate
          : selectedParcelTemplate;

      dimensions = (
        <Subtext>
          <div className="mr-1">
            {t('orders:calculateRates:tabs:shipmentInfo:dimensionsSubtext')}
          </div>
          <FieldsWrapper>{getDimensionsString(dimensionsProvider)}</FieldsWrapper>
        </Subtext>
      );
    }

    return dimensions;
  }

  renderWeight() {
    const {
      startingTabIndex,
      useImportedOrderWeight,
      someOrderHasImportedWeight,
      isWeightRequired,
      formMetaData,
      formSyncErrors,
      importedWeight,
      dataAnalyticsContext,
      t,
    } = this.props;

    const label = `${t('orders:calculateRates:tabs:shipmentInfo:packageWeightLabel')}${
      isWeightRequired ? '' : ` (${t('orders:calculateRates:tabs:shipmentInfo:optionalLabel')})`
    }`;

    const errorMessage = formSyncErrors.weight || formSyncErrors.weightUnit;
    const showErrors = formMetaData?.weight?.touched && errorMessage;
    const errors = showErrors ? <FormError>{errorMessage}</FormError> : null;
    const importedWeightIsNaN = isNaN(Number(importedWeight));

    return (
      <React.Fragment>
        <FieldLabel>{label}</FieldLabel>
        <FieldsWrapper>
          <Field
            name="weight"
            inputRef={this.weightInput}
            component={InputExtendedField}
            placeholder={(importedWeightIsNaN && importedWeight) || 'Weight'}
            size={InputSize.xs}
            autofocus
            disabled={useImportedOrderWeight}
            tabIndex={startingTabIndex}
            dataPendo="package-weight"
            hideErrors
            isResponsive
            isRightAligned
            dataE2E="parcel-form-weight"
            onKeyDown={allowKeyboardNumberInput}
            dataAnalytics={
              dataAnalyticsContext && `${dataAnalyticsContext}PackageInfo-click-weightInput`
            }
            inputMode="decimal"
          />
          <InlineFieldContainer>
            <Field
              name="weightUnit"
              component={SelectField}
              height={HeightSize.medium}
              options={options.weightOptions}
              placeholder="Unit"
              size={InputSize.xs}
              disabled={useImportedOrderWeight}
              tabIndex={startingTabIndex}
              dataPendo="package-weight-unit"
              dataAnalytics={
                dataAnalyticsContext && `${dataAnalyticsContext}PackageInfo-click-weightUnitSelect`
              }
              optionDataAnalytics={
                dataAnalyticsContext && `${dataAnalyticsContext}PackageInfo-click-weightUnitOption`
              }
              hideErrors
            />
          </InlineFieldContainer>
          {someOrderHasImportedWeight && (
            <InlineFieldContainer>
              <Field
                name="useImportedOrderWeight"
                component={CheckboxField}
                labelPosition="right"
                label={
                  <MaxContentLabel>
                    {t('orders:calculateRates:tabs:shipmentInfo:useImportedWeight')}
                  </MaxContentLabel>
                }
                size={InputSize.xs}
                tabIndex={startingTabIndex}
                dataPendo="package-use-imported-weight"
                dataAnalytics={
                  dataAnalyticsContext &&
                  `${dataAnalyticsContext}PackageInfo-click-importedWeightCheckbox`
                }
                onChange={this.onUseImportedWeightChange}
              />
            </InlineFieldContainer>
          )}
        </FieldsWrapper>
        {errors}
        <Subtext>{t('orders:calculateRates:tabs:shipmentInfo:includesPackaging')}</Subtext>
      </React.Fragment>
    );
  }
  packageTemplateRadioChange = (option: Option) => {
    this.setState({ packageDimensionType: option }, () => {
      const setHeightValue = this.getHeightValueByDistanceUnit();
      this.props.changePackageHeight(setHeightValue);
    });
    this.props.changePackageDimenstionType(option);
  };

  getHeightValueByDistanceUnit = () => {
    let polymailerHeight;
    if (this.state?.packageDimensionType?.value === 'polymailer') {
      if (this.props?.getCurrentDistanceUnit.value === 'mm') {
        polymailerHeight = 6.35;
      } else if (this.props?.getCurrentDistanceUnit.value === 'cm') {
        polymailerHeight = 0.635;
      } else {
        polymailerHeight = 0.25;
      }
    } else {
      polymailerHeight = 0;
    }

    return polymailerHeight;
  };

  render() {
    return (
      <>
        <React.Fragment>
          <Field
            name="parcelTemplate"
            component={ParcelSelectFieldContainer}
            onCustomDimensionsSelected={this.handleCustomDimensionsSelected}
            isUsingCustomDimensions={this.props.isUsingCustomDimensions}
            onChange={this.onParcelTemplateSelected}
            disabled={this.props.disableDropdown}
            omitCustomParcelTemplate={this.props.editingCustomTemplate}
            tabIndex={this.props.startingTabIndex}
            dataPendo="package-select"
            showZeroState={this.props.showZeroState}
            dataAnalyticsContext={
              this.props.dataAnalyticsContext && `${this.props.dataAnalyticsContext}PackageInfo`
            }
            appLocation={this.props.appLocation}
          />
          {this.state.shouldShowRadioGroup && this.props.shouldShowPolymailer && (
            <Field
              name="packageDimensionType"
              component={PackageTemplateRadioGroup}
              options={this.packageTypesOptions}
              onChangeCallback={this.packageTemplateRadioChange}
              dimensionType={
                this.state.packageDimensionType
                  ? this.state.packageDimensionType
                  : this.packageTypesOptions[0]
              }
            />
          )}
          {this.renderDimensions()}
          {this.renderWeight()}
        </React.Fragment>
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState, ownProps: Props) => {
  const formValues = getFormValues(ownProps.formName)(state) as PackageTemplateChooserFormValues;
  const mappedState = {
    ...getUserConfigDefaults(state),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formMetaData: getFormMeta(ownProps.formName)(state) as any, // This prop cannot be typed properly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formSyncErrors: getFormSyncErrors(ownProps.formName)(state) as any, // This prop cannot be typed properly
    isUsingCustomDimensions: formValues?.extras?.isUsingCustomDimensions || false,
    selectedParcelTemplate: formValues?.parcelTemplate || null,
    getCurrentDistanceUnit: formValues?.customDimensions?.distanceUnit || null,
    useImportedOrderWeight: formValues?.useImportedOrderWeight || false,
    carrierTemplatewithCustomDimensions:
      formValues?.extras?.carrierTemplateWithCustomDimensions || false,
  };

  return mappedState;
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: Props) => {
  return {
    fetchParcelTemplates: () => dispatch(fetchParcelTemplates()),
    fetchCarrierParcelTemplates: () => dispatch(fetchCarrierParcelTemplates()),
    onCustomDimensionsSelected: (selected: boolean) =>
      dispatch(change(ownProps.formName, 'extras.isUsingCustomDimensions', selected)),
    onCarrierTemplateWithCustomDimensionsSelected: (selected: boolean) =>
      dispatch(change(ownProps.formName, 'extras.carrierTemplateWithCustomDimensions', selected)),
    changePackageWeight: (packageWeight: string) =>
      dispatch(change(ownProps.formName, 'weight', packageWeight)),
    changePackageDimenstionType: (packageDimensionType: Option) =>
      dispatch(change(ownProps.formName, 'packageDimensionType', packageDimensionType)),
    changePackageWeightUnit: (packageWeightUnitOption: Option) =>
      dispatch(change(ownProps.formName, 'weightUnit', packageWeightUnitOption)),
    changeParcelTemplateName: (parcelTemplateName: string) =>
      dispatch(change(ownProps.formName, 'parcelTemplateName', parcelTemplateName)),
    changePackageTemplateIsSetDefault: (isDefault: boolean) =>
      dispatch(change(ownProps.formName, 'isDefault', isDefault)),
    changeIsWeightRequired: (isWeightRequired: boolean) =>
      dispatch(change(ownProps.formName, 'extras.isWeightRequired', isWeightRequired)),
    changePackageForm: (formValues: PackageTemplateChooserFormValues) => {
      dispatch(initialize(ownProps.formName, formValues));
    },
    changePackageHeight: (heightValue: number) =>
      dispatch(change(ownProps.formName, 'customDimensions.height', heightValue)),
    changeUseImportedWeight: (useImportedWeight: boolean) =>
      dispatch(change(ownProps.formName, 'useImportedOrderWeight', useImportedWeight)),
    removeSelectedParcelTemplate: () => dispatch(change(ownProps.formName, 'parcelTemplate', null)),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  translate('orders'),
)(PackageTemplateChooser) as React.ComponentType<Props>;
